/**
 * *********************************************************
 * NAVLOG SAVE / LOAD CONTROLS
 * *********************************************************
 *
 * Purpose:
 * This component provides:
 *
 * 1. Input field for naming a navlog
 * 2. Save button
 * 3. Saved navlogs pulldown menu
 * 4. Load selected navlog
 * 5. Update existing navlog
 *
 * This component does NOT communicate directly with Firebase.
 * It only communicates with the API helper layer.
 *
 * Flow:
 *
 * React Component
 *   -> navlogApi.js
 *      -> API Route
 *         -> navlogRepository.js
 *            -> Firebase
 *
 * *********************************************************
 */

import React, { useEffect, useState } from "react";

import {
  saveNavlog,
  listNavlogs,
  getNavlog,
  updateNavlog,
  deleteNavlog,
} from "./navlogApi";

function NavlogSaveLoadControls({
  selectedAirplane,
  navlogRows,
  setNavlogRows,
  forecastTerminals,
  setForecastTerminals,
  windsRegion,
  setWindsRegion,
  windsFcst,
  setWindsFcst,

}) {
  /**
   * --------------------------------------------------------
   * LOCAL STATE
   * --------------------------------------------------------
   */

  const [navlogName, setNavlogName] = useState("");

  const [savedNavlogs, setSavedNavlogs] = useState([]);

  const [selectedNavlogId, setSelectedNavlogId] = useState("");

  const [message, setMessage] = useState("");

  /**
   * --------------------------------------------------------
   * AIRPLANE ID
   * --------------------------------------------------------
   */

  const airplaneId = selectedAirplane?.id;

  /**
   * --------------------------------------------------------
   * LOAD SAVED NAVLOGS
   * whenever airplane changes
   * --------------------------------------------------------
   */

  useEffect(() => {
    async function loadSavedNavlogs() {
      try {
        if (!airplaneId) {
          setSavedNavlogs([]);
          return;
        }

        const result = await listNavlogs(airplaneId);

        setSavedNavlogs(result);
      } catch (err) {
        console.error(err);
      }
    }

    loadSavedNavlogs();
  }, [airplaneId]);

  /**
   * --------------------------------------------------------
   * SAVE NEW NAVLOG
   * --------------------------------------------------------
   */

  async function handleSaveNavlog() {
    try {
      setMessage("");

      const result = await saveNavlog({
        airplaneId,
        navlogName,
        navlogRows,
        forecastTerminals,
        windsRegion,
        windsFcst,

      });

      /**
       * refresh saved navlogs
       */
      const updatedList = await listNavlogs(airplaneId);

      setSavedNavlogs(updatedList);

      /**
       * select newly saved navlog
       */
      setSelectedNavlogId(result.id);

      setMessage(`Navlog Saved!!${result.name ? ` Name: ${result.name}` : ""}`);
    } catch (err) {
      console.error(err);

      setMessage(err.message);
    }
  }

  /**
   * --------------------------------------------------------
   * LOAD EXISTING NAVLOG
   * --------------------------------------------------------
   */

  async function handleLoadNavlog(navlogId) {
    try {
      setMessage("");

      const navlog = await getNavlog({
        airplaneId,
        navlogId,
      });

      /**
       * restore navlog state
       */

      setNavlogName(navlog.name || "");
      setNavlogRows(navlog.navlogRows || []);
      setForecastTerminals(navlog.forecastTerminals || []);
      setWindsRegion(navlog.windsRegion || "");
      setWindsFcst(navlog.windsFcst || "");
      //setAltitudes(navlog.altitudes || {});
      setMessage("Navlog loaded.");
    } catch (err) {
      console.error(err);

      setMessage(err.message);
    }
  }

  /**
   * --------------------------------------------------------
   * UPDATE EXISTING NAVLOG
   * --------------------------------------------------------
   */

  async function handleUpdateNavlog() {
    try {
      setMessage("");

      if (!selectedNavlogId) {
        throw new Error("No navlog selected");
      }

      await updateNavlog({
        airplaneId,
        navlogId: selectedNavlogId,
        navlogName,
        navlogRows,
        forecastTerminals,
        windsRegion,
        windsFcst,
      });

      /**
       * reload list
       */
      const updatedList = await listNavlogs(airplaneId);

      setSavedNavlogs(updatedList);

      setMessage("Navlog updated.");
    } catch (err) {
      console.error(err);

      setMessage(err.message);
    }
  }
  /**
 * --------------------------------------------------------
 * DELETE SELECTED NAVLOG
 * --------------------------------------------------------
 */

    async function handleDeleteNavlog() {
      try {
        setMessage("");

        if (!selectedNavlogId) {
          throw new Error("No navlog selected");
        }

        /*
         * Confirm before delete
         
        const confirmed = window.confirm(
          `Delete navlog "${navlogName}" ?`
        );

        if (!confirmed) {
          return;
        }
        */

        await deleteNavlog({
          airplaneId,
          navlogId: selectedNavlogId,
        });

        const updatedList = await listNavlogs(airplaneId);

        setSavedNavlogs(updatedList);

        setSelectedNavlogId("");

        setNavlogName("");

        setMessage("Navlog deleted.");
      } catch (err) {
        console.error(err);

        setMessage(err.message);
      }
    }

  /**
   * --------------------------------------------------------
   * RENDER
   * --------------------------------------------------------
   */

  return (
    <div className="navlog-save-load-box">
      <p style={{fontSize: "14px", marginBottom: "10px", color: "GrayText"}}>
        Supports Full CRUD Operations
      </p>

      {/* =====================================================
          SAVED NAVLOGS SELECT
          ===================================================== */}

      <div className="navlog-save-load-row">

        <label>Saved Navlogs</label>

        <select
          value={selectedNavlogId}
          onChange={(e) => {
            const id = e.target.value;

            setSelectedNavlogId(id);

            if (id) {
              handleLoadNavlog(id);
            }
          }}
        >
          <option value="">
            Select Saved Navlog
          </option>

          {savedNavlogs.map((n) => (
            <option
              key={n.id}
              value={n.id}
            >
              {n.name}
            </option>
          ))}
        </select>
      </div>

      {/* =====================================================
          NAVLOG NAME INPUT
          ===================================================== */}

      <div className="navlog-save-load-row">

        <label>Navlog Name</label>

        <input
          type="text"
          value={navlogName}
          onChange={(e) => setNavlogName(e.target.value)}
          placeholder="Example: KATL to KJAX"
        />
      </div>

      {/* =====================================================
          BUTTONS
          ===================================================== */}

      <div className="navlog-save-load-buttons">

        <button
          type="button"
          onClick={handleSaveNavlog}
        >
          Save New Navlog
        </button>

        <button
          type="button"
          onClick={handleUpdateNavlog}
          disabled={!selectedNavlogId}
        >
          Update Selected Navlog
        </button>

        <button
          type="button"
          onClick={handleDeleteNavlog}
          disabled={!selectedNavlogId}
        >
          Delete Selected Navlog
        </button>
        </div>

      

      {/* =====================================================
          MESSAGE
          ===================================================== */}

      {message && (
        <div className="navlog-save-load-message">
          {message}
        </div>
      )}
    </div>
  );
}

export default NavlogSaveLoadControls;